/**
 * `/e/:token` — the public event catalogue, opened from a broadcast link.
 *
 * A sibling of `s.$token.tsx` and of `login.tsx`, never under `routes/_app/`:
 * no session, no offline machinery, no app shell. Data comes from a plain
 * `fetch`, never the authenticated `apiRequest`.
 *
 * Two things make it NOT a Selection page, and both are deliberate:
 *
 *  - **Nobody is named.** No "Prepared for", no sender, no ref. The link is
 *    built to be forwarded around a buying team, and naming the first person it
 *    reached would be wrong by the second hop.
 *  - **Nothing is submitted.** The tick list exists only to compose a WhatsApp
 *    message. There is no quote-request endpoint under `/api/e`, so a link that
 *    anyone may hold cannot write a row on the lead board.
 */
import { useEffect, useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { SharedEventCatalogue } from '@paradise/shared';
import { z } from 'zod';

import logoUrl from '@/assets/logo-h.png';
import { SharedItemCard } from '@/components/share/shared-item-card';
import {
  ALL_CATEGORIES,
  ANY_PRICE,
  matchesSearch,
  priceBandOf,
  SELECTED_ONLY,
  SharedItemFilters
} from '@/components/share/shared-item-filters';
import { ShortlistBar } from '@/components/share/shortlist-bar';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { Spinner } from '@/components/ui/spinner';
import { enquiryMessage, whatsappShareHref } from '@/lib/share-message';
import { useIncrementalList } from '@/lib/use-incremental-list';

/** Same window as the Selection page — each card carries a ~800px photograph. */
const PAGE_SIZE = 12;

export const Route = createFileRoute('/e/$token')({
  component: EventCataloguePage
});

type LoadState =
  { kind: 'loading' } | { kind: 'ok'; data: SharedEventCatalogue } | { kind: 'gone' } | { kind: 'error' };

async function loadCatalogue(token: string): Promise<LoadState> {
  let response: Response;
  try {
    response = await fetch(`/api/e/${encodeURIComponent(token)}`);
  } catch {
    return { kind: 'error' };
  }

  if (response.status === 200) {
    const body: unknown = await response.json().catch(() => null);
    const parsed = SharedEventCatalogue.safeParse(body);
    return parsed.success ? { kind: 'ok', data: parsed.data } : { kind: 'error' };
  }

  // One dead state, not two, and the body is not read at all. An event link
  // never expires (it is revoked by hand), so the `reason` the Selection page
  // pulls out of the 410 envelope would only ever say one thing here.
  if (response.status === 410) return { kind: 'gone' };

  return { kind: 'error' };
}

/**
 * itemNo -> ticked. A plain set of numbers, unlike the Selection page's
 * quantity-and-note lines: nothing is submitted here, so there is nothing for a
 * quantity to be part of. Asking for one would be a form pretending to be a
 * basket.
 */
type Shortlist = Set<number>;

const StoredShortlist = z.array(z.number().int().positive());

function shortlistKey(token: string): string {
  return `event-shortlist:${token}`;
}

function readShortlist(token: string): Shortlist {
  try {
    const raw = localStorage.getItem(shortlistKey(token));
    if (raw === null) return new Set();
    const parsed = StoredShortlist.safeParse(JSON.parse(raw));
    return parsed.success ? new Set(parsed.data) : new Set();
  } catch {
    return new Set();
  }
}

function writeShortlist(token: string, shortlist: Shortlist): void {
  try {
    localStorage.setItem(shortlistKey(token), JSON.stringify([...shortlist]));
  } catch {
    // Private mode / full storage: the tick just won't survive a reload. Not
    // worth surfacing to a customer mid-browse.
  }
}

/** Stable identity, so the filter memo does not re-run before the fetch lands. */
const EMPTY_ITEMS: SharedEventCatalogue['items'] = [];

function formatRange(startDate: string, endDate: string): string {
  const start = new Date(startDate).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  const end = new Date(endDate).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  return `${start} – ${end}`;
}

function EventCataloguePage() {
  const { token } = Route.useParams();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [shortlist, setShortlist] = useState<Shortlist>(() => new Set());
  const [search, setSearch] = useState('');
  const [categoryKey, setCategoryKey] = useState(ALL_CATEGORIES);
  const [priceBand, setPriceBand] = useState(ANY_PRICE);

  useEffect(() => {
    const saved = readShortlist(token);
    void loadCatalogue(token).then(loaded => {
      setState(loaded);
      if (loaded.kind !== 'ok') {
        setShortlist(saved);
        return;
      }
      // A product archived (or un-tagged) since the customer last visited is no
      // longer on the page. Dropping it here is the only place it can be
      // caught — otherwise the bar counts a piece nothing on screen shows, and
      // the WhatsApp message asks about an item number we no longer sell.
      const present = new Set(loaded.data.items.map(item => item.itemNo));
      const pruned = new Set([...saved].filter(itemNo => present.has(itemNo)));
      setShortlist(pruned);
      if (pruned.size !== saved.size) writeShortlist(token, pruned);
    });
  }, [token]);

  const toggle = (itemNo: number, checked: boolean) => {
    setShortlist(prev => {
      const next = new Set(prev);
      if (checked) next.add(itemNo);
      else next.delete(itemNo);
      writeShortlist(token, next);
      return next;
    });
  };

  // Filtering and windowing sit ABOVE the early returns — rules of hooks.
  const allItems = state.kind === 'ok' ? state.data.items : EMPTY_ITEMS;
  const query = search.trim().toLowerCase();
  const filteredItems = useMemo(
    () =>
      allItems.filter(item => {
        if (categoryKey === SELECTED_ONLY) return shortlist.has(item.itemNo);
        if (categoryKey !== ALL_CATEGORIES && item.categoryName !== categoryKey) return false;
        if (priceBand !== ANY_PRICE && priceBandOf(item) !== priceBand) return false;
        return matchesSearch(item, query);
      }),
    [allItems, categoryKey, priceBand, query, shortlist]
  );
  const {
    visible: visibleItems,
    hasMore,
    sentinelRef
  } = useIncrementalList(filteredItems, PAGE_SIZE, `${categoryKey}|${priceBand}|${query}`);

  if (state.kind === 'loading') {
    return (
      <main className="flex min-h-dvh flex-col items-center justify-center gap-3 p-6">
        <Spinner className="size-6" />
      </main>
    );
  }

  if (state.kind === 'gone') {
    return (
      <main className="flex min-h-dvh flex-col items-center justify-center p-6">
        <Empty>
          <EmptyHeader>
            <EmptyTitle>This catalogue is no longer available</EmptyTitle>
            <EmptyDescription>Please contact The Paradise for our current collection.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </main>
    );
  }

  if (state.kind === 'error') {
    return (
      <main className="flex min-h-dvh flex-col items-center justify-center p-6">
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Could not load</EmptyTitle>
            <EmptyDescription>Something went wrong. Please reload the page or try again shortly.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </main>
    );
  }

  const { data } = state;
  const enquiryHref =
    data.contactWhatsapp === null
      ? null
      : whatsappShareHref(enquiryMessage(data.eventName, [...shortlist]), data.contactWhatsapp);

  return (
    <main className="flex min-h-dvh flex-col gap-6 pb-10">
      <header className="flex flex-col items-center gap-3 p-6 pb-0">
        <img src={logoUrl} alt="The Paradise" className="h-10 w-auto" />
      </header>

      <div className="flex flex-col gap-1 px-6">
        <div className="text-[10px] font-medium tracking-wider text-primary uppercase">Catalogue</div>
        <h1 className="font-heading text-xl font-semibold">{data.eventName}</h1>
        <p className="text-sm text-muted-foreground">
          {data.eventCity} · {formatRange(data.startDate, data.endDate)}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {data.items.length} {data.items.length === 1 ? 'piece' : 'pieces'}
          {data.contactWhatsapp !== null && ' · Tick what interests you, then message us'}
        </p>
      </div>

      {data.items.length > 4 && (
        <div className="px-6">
          <SharedItemFilters
            items={data.items}
            search={search}
            onSearchChange={setSearch}
            categoryKey={categoryKey}
            onCategoryChange={setCategoryKey}
            selectedCount={shortlist.size}
            priceMode={data.priceMode}
            priceBand={priceBand}
            onPriceBandChange={setPriceBand}
          />
        </div>
      )}

      <div className="flex flex-col gap-3 px-6">
        {visibleItems.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nothing here matches that. Clear the search to see all {data.items.length} pieces.
          </p>
        ) : (
          visibleItems.map(item => (
            <SharedItemCard
              key={item.itemNo}
              item={item}
              token={token}
              mediaBase="/api/e"
              mode="enquiry"
              checked={shortlist.has(item.itemNo)}
              onCheckedChange={checked => toggle(item.itemNo, checked)}
            />
          ))
        )}
        {hasMore && <div ref={sentinelRef} aria-hidden="true" className="h-px" />}
      </div>

      {/* No terms block: those are the small print under a priced OFFER, and
          this page makes none — it is a catalogue, and every price on it is
          still subject to the conversation that follows. */}

      <ShortlistBar count={shortlist.size} enquiryHref={enquiryHref} />
    </main>
  );
}
